import './CombineCharts.style.less'

import { memo, useEffect, useState } from 'react'

import Bar from '@/components/Chart/Bar'
import Doughnut from '@/components/Chart/Doughnut'
import Gauge from '@/components/Chart/Gauge'
import HorizontalBar from '@/components/Chart/HorizontalBar'
import { LBF_QUERY_KEY } from '../../Liveboard.constant'
import Line from '@/components/Chart/Line'
import LiveboardSpinner from '../LiveboardSpinner/LiveboardSpinner'
// import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import MixedLine from '@/components/Chart/MixedLine'
import { Moment } from 'moment'
import MultiChart from '@/components/Chart/MultiChart'
import Pie from '@/components/Chart/Pie'
import { Spin } from 'antd'
import { getChartData } from '../../Liveboard.transform'
import { transformPeriod } from './CombineCharts.transform'
import { useAuth } from '@/providers/auth.provider'
import { useQuery } from 'react-query'

interface Prop {
  item: any
  options?: {
    filter: any
    datePicked: Moment[]
  }
}

function CombineCharts(props: Prop) {
  const { item, options = {} as any } = props

  const { user } = useAuth()

  const [periods, setperiods] = useState([])

  const [currentPeriod, setcurrentPeriod] = useState('monthly')

  const initialChart = {
    labels: '',
    datasets: [],
  }

  const {
    data: dataChart,
    isLoading,
    isFetching,
  } = useQuery(
    [LBF_QUERY_KEY.QSF_CHART_DATA, item, options, currentPeriod],
    () => getChartData(item, user.company.id, options, currentPeriod)
  )

  useEffect(() => {
    setperiods(transformPeriod(props?.options?.datePicked))
  }, [props?.options?.datePicked])

  switch (item.chart_type) {
    case 'line':
      return (
        <>
          <Spin
            spinning={isFetching || isLoading}
            indicator={<LiveboardSpinner />}
          >
            <Line
              target={item?.target}
              id={item.chart_type}
              showLegend={item?.show_legend ?? true}
              data={dataChart ?? initialChart}
              title={item.name}
              periods={periods}
              onPeriodChange={(period) => {
                setcurrentPeriod(period)
              }}
            />
          </Spin>
        </>
      )
    case 'area':
      return (
        <>
          <Spin
            spinning={isFetching || isLoading}
            indicator={<LiveboardSpinner />}
          >
            <Line
              target={item?.target}
              id={item.chart_type}
              sign="%"
              data={dataChart ?? initialChart}
              title={item.name}
              periods={periods}
              showLegend={item?.show_legend ?? true}
              onPeriodChange={(period) => {
                setcurrentPeriod(period)
              }}
            />
          </Spin>
        </>
      )
    case 'column':
      return (
        <>
          <Spin
            spinning={isFetching || isLoading}
            indicator={<LiveboardSpinner />}
          >
            <Bar
              id={item.chart_type}
              data={isFetching ? initialChart : dataChart}
              title={item.name}
              showLegend={item?.show_legend ?? true}
              periods={periods}
              onPeriodChange={(period) => {
                setcurrentPeriod(period)
              }}
            />
          </Spin>
        </>
      )
    case 'hundred_percent_stacked_column':
      return (
        <>
          <Spin
            spinning={isFetching || isLoading}
            indicator={<LiveboardSpinner />}
          >
            <Bar
              id={item.chart_type}
              data={isFetching ? initialChart : dataChart}
              stacked
              showLegend={item?.show_legend ?? true}
              title={item.name}
              periods={periods}
              onPeriodChange={(period) => {
                setcurrentPeriod(period)
              }}
            />
          </Spin>
        </>
      )
    case 'two_column':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <Bar
            id={item.chart_type}
            data={isFetching ? initialChart : dataChart}
            title={item.name}
            showLegend={item?.show_legend ?? true}
            periods={periods}
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
          />
        </Spin>
      )

    case 'column_and_line':
      return (
        <>
          <Spin
            spinning={isFetching || isLoading}
            indicator={<LiveboardSpinner />}
          >
            <MixedLine
              id={item.chart_type}
              title={item.name}
              showLegend={item?.show_legend ?? true}
              data={isFetching ? initialChart : dataChart}
              periods={periods}
              onPeriodChange={(period) => {
                setcurrentPeriod(period)
              }}
            />
          </Spin>
        </>
      )
    case 'bar':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <HorizontalBar
            target={item?.target}
            id={item.chart_type}
            showLegend={item?.show_legend ?? true}
            data={isFetching ? initialChart : dataChart}
            title={item.name}
            periods={periods}
            sign="%"
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
          />
        </Spin>
      )
    case 'hundred_percent_stacked_bar':
      return (
        <>
          <Spin
            spinning={isFetching || isLoading}
            indicator={<LiveboardSpinner />}
          >
            <HorizontalBar
              target={item?.target}
              id={item.chart_type}
              showLegend={item?.show_legend ?? true}
              data={isFetching ? initialChart : dataChart}
              title={item.name}
              periods={periods}
              onPeriodChange={(period) => {
                setcurrentPeriod(period)
              }}
              sign="%"
              stacked
            />
          </Spin>
        </>
      )
    case 'pie':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <Pie
            id={item.chart_type}
            sign="%"
            title={item.name}
            showLegend={item?.show_legend ?? true}
            data={isFetching ? initialChart : dataChart}
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
          />
        </Spin>
      )
    case 'doughnut':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <Doughnut
            id={item.chart_type}
            sign="%"
            title={item.name}
            showLegend={item?.show_legend ?? true}
            data={isFetching ? initialChart : dataChart}
            periods={periods}
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
          />
        </Spin>
      )
    case 'gauge':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <Gauge
            id={item.chart_type}
            target={item?.target}
            sign="%"
            showLegend={item?.show_legend ?? true}
            data={isFetching ? initialChart : dataChart}
            title={item.name}
            periods={periods}
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
          />
        </Spin>
      )
    case 'two_bar':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <HorizontalBar
            id={item.chart_type}
            showLegend={item?.show_legend ?? true}
            data={isFetching ? initialChart : dataChart}
            title={item.name}
            periods={periods}
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
            sign="%"
          />
        </Spin>
      )
    case 'multi':
      return (
        <Spin
          spinning={isFetching || isLoading}
          indicator={<LiveboardSpinner />}
        >
          <MultiChart
            target={item?.target}
            chartTypeData={item.chart_type_data ?? []}
            id={item.chart_type}
            item={item}
            showLegend={item?.show_legend ?? false}
            data={isFetching ? initialChart : dataChart}
            title={item.name}
            periods={periods}
            onPeriodChange={(period) => {
              setcurrentPeriod(period)
            }}
            sign={item?.sign}
            datePicked={options?.datePicked}
            currentPeriod={currentPeriod}
          />
        </Spin>
      )
    default:
      break
  }
}

export default memo(CombineCharts)
